import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Zap, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface WorkflowFormTriggersProps {
  workflowId: string;
}

export function WorkflowFormTriggers({ workflowId }: WorkflowFormTriggersProps) {
  const { orgId } = useAuth();
  const [triggers, setTriggers] = useState<any[]>([]);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [formTemplates, setFormTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addTriggerOpen, setAddTriggerOpen] = useState(false);

  // Form state for new trigger
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [triggerOn, setTriggerOn] = useState<"enter_stage" | "exit_stage">("enter_stage");
  const [isRequired, setIsRequired] = useState(true);

  useEffect(() => {
    if (orgId) {
      loadData();
    }
  }, [orgId, workflowId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load workflows
      const { data: workflowsData, error: workflowsError } = await supabase
        .from("workflows")
        .select("*")
        .eq("org_id", orgId);

      if (workflowsError) throw workflowsError;
      setWorkflows(workflowsData || []);

      // Load stages
      const { data: stagesData, error: stagesError } = await supabase
        .from("workflow_stages")
        .select("*")
        .eq("workflow_id", workflowId)
        .order("stage_order");

      if (stagesError) throw stagesError;
      setStages(stagesData || []);

      // Load form templates from database
      const { data: templatesData, error: templatesError } = await supabase
        .from("form_templates")
        .select("*")
        .or(`org_id.eq.${orgId},is_system_template.eq.true`);

      if (templatesError) throw templatesError;
      setFormTemplates(templatesData || []);

      // Load triggers
      const { data: triggersData, error: triggersError } = await supabase
        .from("form_triggers")
        .select(`
          *,
          form_template:form_templates(*),
          stage:workflow_stages(*)
        `)
        .eq("workflow_id", workflowId);

      if (triggersError) throw triggersError;
      setTriggers(triggersData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load workflow triggers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrigger = async () => {
    if (!selectedFormId || !selectedStageId) {
      toast.error("Please select both a form and a stage");
      return;
    }

    try {
      const { error } = await supabase.from("form_triggers").insert({
        form_template_id: selectedFormId,
        workflow_id: workflowId,
        stage_id: selectedStageId,
        trigger_on: triggerOn,
        is_required: isRequired,
        org_id: orgId,
      });

      if (error) throw error;

      toast.success("Form trigger added successfully");
      setAddTriggerOpen(false);
      setSelectedFormId("");
      setSelectedStageId("");
      loadData();
    } catch (error) {
      console.error("Error adding trigger:", error);
      toast.error("Failed to add form trigger");
    }
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    if (!confirm("Are you sure you want to delete this form trigger?")) return;

    try {
      const { error } = await supabase
        .from("form_triggers")
        .delete()
        .eq("id", triggerId);

      if (error) throw error;

      toast.success("Form trigger deleted");
      loadData();
    } catch (error) {
      console.error("Error deleting trigger:", error);
      toast.error("Failed to delete form trigger");
    }
  };

  const handleToggleRequired = async (triggerId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from("form_triggers")
        .update({ is_required: !currentValue })
        .eq("id", triggerId);

      if (error) throw error;

      toast.success("Form trigger updated");
      loadData();
    } catch (error) {
      console.error("Error updating trigger:", error);
      toast.error("Failed to update form trigger");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading form triggers...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Automated Form Triggers
          </h3>
          <p className="text-sm text-muted-foreground">
            Automatically require forms when jobs reach specific workflow stages
          </p>
        </div>
        <Dialog open={addTriggerOpen} onOpenChange={setAddTriggerOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              Add Trigger
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Form Trigger</DialogTitle>
              <DialogDescription>
                Automatically attach a form when a job enters or exits a workflow stage
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Form Template</Label>
                <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a form" />
                  </SelectTrigger>
                  <SelectContent>
                    {formTemplates.map((form) => (
                      <SelectItem key={form.id} value={form.id}>
                        {form.name} ({form.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Workflow Stage</Label>
                <Select value={selectedStageId} onValueChange={setSelectedStageId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.stage_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Trigger When</Label>
                <Select value={triggerOn} onValueChange={(val: any) => setTriggerOn(val)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enter_stage">Job enters this stage</SelectItem>
                    <SelectItem value="exit_stage">Job exits this stage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="required"
                  checked={isRequired}
                  onCheckedChange={setIsRequired}
                />
                <Label htmlFor="required">Make this form required</Label>
              </div>

              <Button onClick={handleAddTrigger} className="w-full">
                Add Trigger
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {triggers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No form triggers yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Set up automated form requirements for workflow stages
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {triggers.map((trigger) => (
            <Card key={trigger.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {trigger.form_template?.name}
                      {trigger.is_required && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Required
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Triggers when job{" "}
                      <span className="font-medium">
                        {trigger.trigger_on === "enter_stage" ? "enters" : "exits"}
                      </span>{" "}
                      stage: <span className="font-medium">{trigger.stage?.stage_name}</span>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={trigger.is_required}
                      onCheckedChange={() =>
                        handleToggleRequired(trigger.id, trigger.is_required)
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTrigger(trigger.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
